import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  TIMBER_SPECIES, TIMBER_TYPES, DOOR_TYPES, BRICK_TYPES,
  METAL_TYPES, WINDOW_GLAZING, ROOF_PROFILES,
} from '../pages/createListingConstants';

/**
 * Category-specific attribute fields shown on the listing create/edit form.
 * Only renders the conditional block matching the currently selected category.
 *
 * Extracted from CreateListingPage for readability — the parent component
 * still owns formData state.
 */
export default function CategoryAttributesSection({ formData, setFormData, categories }) {
  if (!formData.category) return null;

  const updateAttr = (key, value) =>
    setFormData({
      ...formData,
      specific_attributes: { ...formData.specific_attributes, [key]: value },
    });

  const attrs = formData.specific_attributes || {};

  return (
    <div className="p-6 rounded-lg bg-card/50 border border-border/20 space-y-6">
      <h2 className="font-playfair text-xl font-semibold">
        Category-Specific Details
        <span className="ml-2 text-sm font-normal text-muted-foreground">
          ({categories.find(c => c.id === formData.category)?.name})
        </span>
      </h2>

      {/* TIMBER/BEAMS FIELDS */}
      {formData.category === 'beams' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Timber Type</label>
            <Select
              value={attrs.timber_type || ''}
              onValueChange={(v) => updateAttr('timber_type', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select timber type" />
              </SelectTrigger>
              <SelectContent>
                {TIMBER_TYPES.map((type) => (
                  <SelectItem key={type} value={type.toLowerCase()}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Species</label>
            <Select
              value={attrs.species || ''}
              onValueChange={(v) => updateAttr('species', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select species" />
              </SelectTrigger>
              <SelectContent>
                {TIMBER_SPECIES.map((species) => (
                  <SelectItem key={species} value={species.toLowerCase()}>{species}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attrs.treated || false}
                onChange={(e) => updateAttr('treated', e.target.checked)}
                className="w-4 h-4 rounded border-border/40 text-accent"
              />
              <span className="text-sm">Treated Timber</span>
            </label>
          </div>
        </div>
      )}

      {/* DOORS FIELDS */}
      {formData.category === 'doors' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Door Type</label>
            <Select
              value={attrs.door_type || ''}
              onValueChange={(v) => updateAttr('door_type', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select door type" />
              </SelectTrigger>
              <SelectContent>
                {DOOR_TYPES.map((type) => (
                  <SelectItem key={type} value={type.toLowerCase()}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attrs.glazed || false}
                onChange={(e) => updateAttr('glazed', e.target.checked)}
                className="w-4 h-4 rounded border-border/40 text-accent"
              />
              <span className="text-sm">Glazed</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attrs.hardware_included || false}
                onChange={(e) => updateAttr('hardware_included', e.target.checked)}
                className="w-4 h-4 rounded border-border/40 text-accent"
              />
              <span className="text-sm">Hardware Included</span>
            </label>
          </div>
        </div>
      )}

      {/* BRICKS FIELDS */}
      {formData.category === 'bricks' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Brick Type</label>
            <Select
              value={attrs.brick_type || ''}
              onValueChange={(v) => updateAttr('brick_type', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select brick type" />
              </SelectTrigger>
              <SelectContent>
                {BRICK_TYPES.map((type) => (
                  <SelectItem key={type} value={type.toLowerCase()}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Color/Tone</label>
            <input
              type="text"
              value={attrs.color || ''}
              onChange={(e) => updateAttr('color', e.target.value)}
              className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg"
              placeholder="e.g., Red, Cream, Charcoal"
            />
          </div>
        </div>
      )}

      {/* WINDOWS FIELDS */}
      {formData.category === 'windows' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Glazing Type</label>
            <Select
              value={attrs.glazing_type || ''}
              onValueChange={(v) => updateAttr('glazing_type', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select glazing" />
              </SelectTrigger>
              <SelectContent>
                {WINDOW_GLAZING.map((glazing) => (
                  <SelectItem key={glazing} value={glazing.toLowerCase()}>{glazing}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attrs.hardware_complete || false}
                onChange={(e) => updateAttr('hardware_complete', e.target.checked)}
                className="w-4 h-4 rounded border-border/40 text-accent"
              />
              <span className="text-sm">Hardware Complete</span>
            </label>
          </div>
        </div>
      )}

      {/* ROOFING FIELDS */}
      {formData.category === 'roofing' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Profile Type</label>
            <Select
              value={attrs.profile || ''}
              onValueChange={(v) => updateAttr('profile', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select profile" />
              </SelectTrigger>
              <SelectContent>
                {ROOF_PROFILES.map((profile) => (
                  <SelectItem key={profile} value={profile.toLowerCase()}>{profile}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Color</label>
            <input
              type="text"
              value={attrs.color || ''}
              onChange={(e) => updateAttr('color', e.target.value)}
              className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg"
              placeholder="e.g., Colorbond Monument, Terracotta"
            />
          </div>
        </div>
      )}

      {/* FIXTURES/HARDWARE - METAL TYPE */}
      {(formData.category === 'fixtures' || formData.category === 'hardware') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Metal Type (if applicable)</label>
            <Select
              value={attrs.metal_type || ''}
              onValueChange={(v) => updateAttr('metal_type', v)}
            >
              <SelectTrigger className="h-12 bg-secondary/30 border-border/40">
                <SelectValue placeholder="Select metal type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not_applicable">Not Applicable</SelectItem>
                {METAL_TYPES.map((metal) => (
                  <SelectItem key={metal} value={metal.toLowerCase()}>{metal}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attrs.working_condition || false}
                onChange={(e) => updateAttr('working_condition', e.target.checked)}
                className="w-4 h-4 rounded border-border/40 text-accent"
              />
              <span className="text-sm">In Working Condition</span>
            </label>
          </div>
        </div>
      )}

      {/* FLOORING FIELDS */}
      {formData.category === 'flooring' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Pattern (if applicable)</label>
            <input
              type="text"
              value={attrs.pattern || ''}
              onChange={(e) => updateAttr('pattern', e.target.value)}
              className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg"
              placeholder="e.g., Herringbone, Checkerboard"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Coverage Area Available</label>
            <input
              type="text"
              value={attrs.coverage_area || ''}
              onChange={(e) => updateAttr('coverage_area', e.target.value)}
              className="w-full h-12 px-4 bg-secondary/30 border border-border/40 rounded-lg"
              placeholder="e.g., 25 sqm"
            />
          </div>
        </div>
      )}
    </div>
  );
}
